import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { printerStages, printerStatusInfo, parsePrinterStatus } from "shared/docuprintStatus";
import { productionStages, stageIndex } from "shared/productionStages";
import "./tracking.css";

const checkedTime = (value) => {
  if (!value || Number.isNaN(Date.parse(value))) return "Primeira consulta em andamento";
  return `Consultado em ${new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};
export default function ProductionTracking({ item, printer, paid = false, cancelled = false }) {
  const linked = Boolean(printer?.id);
  const providerCode = parsePrinterStatus(printer?.providerStatus);
  const info = printerStatusInfo(providerCode);
  const exception = providerCode === -1 || providerCode === 6;
  const manual = !linked && stageIndex(item?.stage) > 0;
  const steps = linked ? printerStages : manual ? productionStages : [];
  const active = linked ? providerCode : stageIndex(item?.stage);
  const title = linked
    ? info.title
    : manual
    ? productionStages[active].label
    : cancelled
    ? "Pedido cancelado"
    : !paid
    ? "Aguardando pagamento"
    : printer?.state === "blocked"
    ? "Pendência no envio à gráfica"
    : printer?.state === "unavailable"
    ? "Acompanhamento temporariamente indisponível"
    : printer?.state === "awaiting_invoice"
    ? "Preparando os documentos do pedido"
    : printer?.state === "uncertain" || printer?.state === "sending"
    ? "Conferindo envio à gráfica"
    : "Preparando envio à gráfica";
  return (
    <section className="print-tracking" aria-label="Acompanhamento de produção">
      <div className="print-tracking__heading">
        <div>
          <span className="print-tracking__eyebrow">
            {linked
              ? "DIRETO DA GRÁFICA"
              : manual
              ? "ATUALIZAÇÃO DA EQUIPE PSO"
              : "ACOMPANHAMENTO DO PEDIDO"}
          </span>
          <h3 className={exception ? "print-tracking__error" : ""}>{title}</h3>
          <p>
            {linked
              ? checkedTime(printer.checkedAt)
              : manual
              ? "Etapa registrada pela equipe responsável."
              : "O andamento aparecerá aqui assim que o pedido avançar."}
          </p>
        </div>
        {linked && (
          <span className="print-tracking__reference">
            Gráfica <strong>#{printer.id}</strong>
          </span>
        )}
      </div>
      {linked && (printer.stale || printer.syncFailed) && (
        <div className="print-tracking__notice" role="status">
          <InfoOutlinedIcon fontSize="small" />
          {printer.checkedAt
            ? "Exibindo a última informação recebida. A consulta será atualizada novamente."
            : "Consultando o andamento na gráfica. Atualize em instantes."}
        </div>
      )}
      {exception && (
        <div className="print-tracking__notice print-tracking__notice--error">
          <InfoOutlinedIcon fontSize="small" />
          {providerCode === -1
            ? "A gráfica apontou uma pendência de integração. A equipe PSO precisa verificar o pedido."
            : "A gráfica informou o cancelamento deste pedido."}
        </div>
      )}
      {!linked && printer?.state === "blocked" && (
        <div className="print-tracking__notice">
          <InfoOutlinedIcon fontSize="small" />O pedido precisa de uma verificação pela equipe PSO
          antes de seguir para a gráfica.
        </div>
      )}
      {!exception && steps.length > 0 && (
        <ol
          className="print-tracking__steps"
          aria-label="Etapas do pedido"
          style={{ "--step-count": steps.length }}
        >
          {steps.map((step, index) => {
            const completed = active !== null && index < active;
            const current = active !== null && index === active;
            return (
              <li
                key={step.code ?? step.key}
                className={`${completed ? "is-complete" : ""} ${current ? "is-current" : ""}`}
                aria-current={current ? "step" : undefined}
              >
                <span className="print-tracking__dot" aria-hidden="true">
                  {completed ? <CheckRoundedIcon /> : index + 1}
                </span>
                <div>
                  <span className="print-tracking__step-label">{step.label}</span>
                  <small>
                    {completed ? "Concluído" : current ? "Etapa atual" : "Próxima etapa"}
                  </small>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {linked && providerCode === 5 && (
        <p className="print-tracking__footnote">
          A coleta foi confirmada pela gráfica. A entrega ao comprador depende do acompanhamento da
          transportadora.
        </p>
      )}
      {printer?.trackingNumber && (
        <div className="print-tracking__shipping">
          <LocalShippingOutlinedIcon />
          <div>
            <span>Rastreio da transportadora</span>
            <strong>{printer.trackingNumber}</strong>
          </div>
        </div>
      )}
      {!linked && manual && Boolean(item.history?.length) && (
        <Box mt={2}>
          {item.history.map((entry, index) => (
            <Typography key={index} variant="caption" display="block" color="text.secondary">
              {new Date(entry.createdAt).toLocaleString("pt-BR")} ·{" "}
              {productionStages.find((stage) => stage.key === entry.stage)?.label}
              {entry.note ? ` — ${entry.note}` : ""}
            </Typography>
          ))}
        </Box>
      )}
    </section>
  );
}
